/**
 * Plot type → SciChart renderable-series mapping.
 *
 * `seriesKind` picks the Fast*RenderableSeries; `dataKind` picks the DataSeries
 * shape (which columns the transport must supply for this plot):
 *   - "ohlc" → OhlcDataSeries (open/high/low/close)
 *   - "xy"   → XyDataSeries (single y)
 *   - "xyy"  → XyyDataSeries (y + y1, for bands)
 */

import type { Plot } from "@qufolio/model";

export type SeriesKind =
  | "candlestick"
  | "ohlc"
  | "line"
  | "mountain"
  | "column"
  | "band"
  | "scatter";

export type DataKind = "ohlc" | "xy" | "xyy";

export interface SeriesTypePlan {
  seriesKind: SeriesKind;
  dataKind: DataKind;
}

const SERIES_BY_PLOT_TYPE: Record<Plot["type"], SeriesTypePlan> = {
  candlestick: { seriesKind: "candlestick", dataKind: "ohlc" },
  ohlc: { seriesKind: "ohlc", dataKind: "ohlc" },
  line: { seriesKind: "line", dataKind: "xy" },
  mountain: { seriesKind: "mountain", dataKind: "xy" },
  column: { seriesKind: "column", dataKind: "xy" },
  // A histogram renders as columns; the distinction is only in the DSL verb.
  histogram: { seriesKind: "column", dataKind: "xy" },
  band: { seriesKind: "band", dataKind: "xyy" },
  scatter: { seriesKind: "scatter", dataKind: "xy" },
  // Markers, like a scatter — but the y it plots is a lane rather than the
  // column's own value, and one column becomes *two* series split by sign. See
  // `plan/signal.ts`; the shape mapping is all that belongs here.
  signal: { seriesKind: "scatter", dataKind: "xy" },
  // The transpose, and the one entry whose mapping is a placeholder rather than
  // a description. Volume-by-Price is drawn by hand from `plan/volume-by-price.ts`
  // bins — horizontal rectangles positioned by price and sized by volume — so it
  // has no Fast*RenderableSeries and no DataSeries of its own to name, and the
  // transport owes it no column: it reads the closes and volumes the candles
  // already loaded. `xy` is chosen over the truer-sounding `ohlc` because
  // `dataKind === "ohlc"` is what `y-fit` reads as *this series is the price*,
  // and an empty OHLC series claiming to be the price would hand the axis fit
  // nothing to measure. An empty `xy` contributes nothing to the fit, which is
  // exactly right for an overlay whose geometry is not a function of x.
  volumeByPrice: { seriesKind: "column", dataKind: "xy" },
  // Shapes a detector found — trendlines, later chart patterns — drawn as
  // derived annotations from the response's `qf.geometry`, never as a series.
  // The placeholder is Volume-by-Price's, for its reason: an empty `xy` is
  // nothing to the Y fit and owes the transport no column.
  // `docs/plans/chart-patterns.md`.
  geometry: { seriesKind: "column", dataKind: "xy" },
  // A column that colours the price candles instead of drawing (HCD/LCD). Its
  // series is a placeholder the builder keeps invisible and out of the Y fit;
  // the values reach the candles through `repaintCandles`.
  paint: { seriesKind: "column", dataKind: "xy" },
};

export function resolveSeriesType(type: Plot["type"]): SeriesTypePlan {
  return SERIES_BY_PLOT_TYPE[type];
}